import React from 'react';
import styled from 'styled-components';

// Shared "pick one" card. Promoted from src/features/scoreBackfill/SelectableCard.js (which now
// re-exports this) so two features can share it without a cross-feature import.
//
// The `disabled` + `reason` variant exists for the score-spec kind pickers, where a choice can be
// unavailable for a reason the operator needs to READ: a documents kind that requires a payload
// field the chosen trigger does not provide, or a kind the loader's guard has not enabled yet.
// A disabled card that just greys out teaches nothing; the reason is the whole point.
//
// The children row keeps the original layout contract (a radio/checkbox and its text as flex
// siblings); the reason renders as a second row beneath it, so existing callers are unaffected.
//
// Selectable/interactive surfaces use the `border` token; passive info panels use
// `lowContrastBorder`.

const Card = styled.label`
  display: flex;
  flex-direction: column;
  padding: 12px 14px;
  border: 1px solid
    ${({ theme, $selected, $disabled }) => {
      if ($disabled) return theme.themeColors.lowContrastBorder;
      return $selected ? theme.themeColors.primaryCtaButton : theme.themeColors.border;
    }};
  border-radius: 6px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  background: ${({ theme, $disabled }) => ($disabled ? theme.themeColors.disabled : 'transparent')};

  &:focus-within {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 2px;
  }

  input {
    margin-top: 3px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const Reason = styled.div`
  font-size: 11px;
  line-height: 1.45;
  margin-top: 6px;
  padding-left: 32px;
  color: ${({ theme }) => theme.themeColors.secondaryText};
`;

export const SelectableCard = ({ selected, disabled, reason, children, ...rest }) => (
  <Card $selected={selected} $disabled={disabled} aria-disabled={disabled || undefined} {...rest}>
    <Row>{children}</Row>
    {reason ? <Reason>{reason}</Reason> : null}
  </Card>
);
